import React, { ReactElement } from 'react'
import Tooltip from './Tooltip'
import TimeText from './TimeText'
import Table from './Table'
import { ExternalLinkText } from '../views/Events'
import { formatExplorerUrl } from '../util/Chains'
import { ManagedPaymentMethod } from '../util/Managed'
import { shortAddress, trimZeros } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import LinkExternal from './LinkExternal'
import { HoverInfoIcon } from '../icons/Info'
import { Section, SectionBreaker, SectionContent, SectionItem, SubTitle } from './Shared'

interface WithdrawalDetailsProps {
  paymentMethod: ManagedPaymentMethod
  requestedAt: string
  network: string
  amount: string
  logo: string
  isFiatWithdrawal: boolean
  fiat: {
    transferMethod: string
    currencyOut: 'EUR' | 'USD'
    fee: number
    receiptAccountId?: string
    finalAmount?: string
  }
  networkFeeAmount?: string
  finalAmountCrypto?: string
  completedAt?: string
  transactionHash?: string
}

const WithdrawalDetails =
({
  paymentMethod,
  requestedAt,
  completedAt,
  transactionHash,
  network,
  networkFeeAmount,
  finalAmountCrypto,
  amount,
  fiat,
  isFiatWithdrawal,
  logo
}: WithdrawalDetailsProps): ReactElement => {
  return <Section>
    <SubTitle style={{ fontSize: 18 }}>
        Withdrawal details
    </SubTitle>

    {
      isFiatWithdrawal
        ? <SectionContent>
          <SectionItem>
            <SubTitle>Source currency</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{paymentMethod.ticker}</span>
            </div>
          </SectionItem>
          <SectionItem>
            <SubTitle>Destination currency</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={logo } style={{ height: 18, width: 18 }} />
              <span>{fiat.currencyOut}</span>
            </div>
          </SectionItem>
          {fiat.finalAmount && <SectionItem>
            <SubTitle>Final withdrawal amount</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={logo } style={{ height: 18, width: 18 }} />
              <span>{trimZeros(fiat.finalAmount)}</span>
            </div>
          </SectionItem>}
          <SectionItem>
            <SubTitle>Fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{trimZeros(fiat.fee.toString())}</span>
            </div>
          </SectionItem>
          <SectionItem>
            <SubTitle>
              <span>Requested date</span>
            </SubTitle>
            <TimeText key="0" date={new Date(requestedAt)} />
          </SectionItem>
        </SectionContent>
        : <SectionContent>
          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{paymentMethod.ticker}</span>
            </div>
          </SectionItem>

          {networkFeeAmount && <SectionItem>
            <SubTitle>
              <span>Network fee amount</span>
              <Tooltip message='Fee collected by network.'>
                <HoverInfoIcon width={17} />
              </Tooltip>
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{trimZeros(networkFeeAmount)}</span>
            </div>
          </SectionItem>}

          {finalAmountCrypto && <SectionItem>
            <SubTitle>
              <span>Final amount</span>
              <Tooltip message='The amount you receive after fee deduction.' >
                <HoverInfoIcon width={17} />
              </Tooltip>
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{trimZeros(finalAmountCrypto)}</span>
            </div>
          </SectionItem>}

          <SectionItem>
            <SubTitle>
              <span>Requested date</span>
            </SubTitle>
            <TimeText key="0" date={new Date(requestedAt)} />
          </SectionItem>
        </SectionContent>
    }

    <SectionBreaker />
    {isFiatWithdrawal && fiat.finalAmount && <div>
      <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Conversion rate</SubTitle>

      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span>1</span>
          <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
          {paymentMethod.ticker}
        </div>
        <div>=</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span>{trimZeros((Number(fiat.finalAmount) / Number(amount)).toFixed(6))}</span>
          <img src={logo } style={{ height: 18, width: 18 }} />
          {fiat.currencyOut}
        </div>
      </div>
    </div>}

    {transactionHash && completedAt && <div>
      <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Withdrawal transactions</SubTitle>
      <Table
        disableHoverStyle
        headers={[
          <span key="key">Transaction hash</span>,
          <span key="network">Network</span>,
          <span key="amount">Asset</span>,
          <span key="date">Completed date</span>
        ]}
        items={
          [
            [
              <div key="1">
                <ExternalLinkText
                  style={
                    { textDecoration: 'underline', display: 'flex', alignItems: 'center' }
                  }
                  onClick={e => e.stopPropagation()}
                  target="_blank" rel="noreferrer"
                  href={formatExplorerUrl(
                    network,
                    'tx',
                    transactionHash
                  )}>
                  {shortAddress(transactionHash ?? '', 18)}
                  <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                </ExternalLinkText>
              </div>,
              <div key="2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <img src={paymentMethod.hostChain?.logo ?? paymentMethod.logo} style={{ height: 18, width: 18 }} />
                  <span>
                    { paymentMethod.hostChain?.name ?? paymentMethod.name }
                  </span>
                </div>
              </div>,
              <div key="3" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
                <span>{trimZeros(amount)}</span>
              </div>,
              <TimeText key="1" date={new Date(completedAt)} />
            ]
          ]
        }
        isLoading={false}
      />
    </div>}

  </Section>
}

export default WithdrawalDetails
